// Dependencies
import { Map, fromJS } from "immutable";
import Immutable, { isImmutable } from "immutable";
import React, { useMemo } from "react";
import { ActionMeta } from "react-select";

// Components
import AjaxInput, { SelectOption } from "./index";

interface BaseProps {
	label?: string;
	disabled?: boolean;
	name?: string;
	placeholder?: string;
	inputId?: string;
	search?: (inputValue: string) => Promise<SelectOption[]>;
	onChange?: (value: string | Map<string, string>, isSelected: boolean, selectionID?: string) => void;
	isSelected?: boolean;
	prependOptions?: Immutable.List<Immutable.Map<string, string>> | { id: string; name: string }[];
	results?: any;
	value?: string | Immutable.Map<string, string> | Immutable.List<Immutable.Map<string, string>>; // string or immutable map
	limit?: number;
	isMultiSelect?: boolean;
	filters?: { [key: string]: string };
	onDelete?: (selectionID: string) => void; // only used when isMultiSelect is true and used for old select picker compatibility
	isInputPool?: boolean;
	type:
		| "department"
		| "templates"
		| "reviewForms"
		| "goalCategories"
		| "goalOutlook"
		| "reviewFormNames"
		| "workflowTemplates";
}

/**
 * This component is used to wrap the EmployeePicker component and make it compatible with the
 * old SelectPicker component. They work differently so this component is used to make the
 * transition easier. Ideally we don't need to use this component and anything that uses it
 * should be refactored to use the EmployeePicker component directly.
 */
const CompatibilityWrapper = (props: BaseProps) => {
	const { disabled = false, placeholder = "", isMultiSelect = false, filters, isInputPool, label = "" } = props;

	/**
	 * The old employee picker component could have a value of a string or an
	 * immutable map. If the value was a string, it meant there was no selection
	 * and it was just text input for the search. If the value was an immutable
	 * map, it meant there was a selection and the value was the employee's ID.
	 * @param value
	 * @param actionMeta
	 */
	const onChange = (value: SelectOption | SelectOption[], actionMeta: ActionMeta<SelectOption>) => {
		switch (actionMeta.action) {
			case "select-option":
				// to make it compatible with old select picker,
				// we need to return the one value added in multi select
				// instead of the entire array of values
				if (isMultiSelect) {
					const multiVal = value as SelectOption[];

					if (isInputPool) {
						props.onChange &&
							props.onChange(
								fromJS({
									input: "",
									pool: multiVal.map((val) => ({
										...val,
										id: val.value,
										name: val.label,
									})),
								}) as any,
								true
							);
					} else {
						const lastVal = multiVal[multiVal.length - 1];
						props.onChange &&
							props.onChange(
								fromJS({ ...lastVal, id: lastVal.value, name: lastVal.label }),
								true,
								lastVal.value
							);
					}
				} else {
					const singleVal = value as SelectOption;
					props.onChange &&
						props.onChange(
							fromJS({ ...value, id: singleVal.value, name: singleVal.label }) as any,
							true,
							singleVal.value
						);
				}
				break;
			case "remove-value": {
				const multiVal = value as SelectOption[];
				if (isInputPool) {
					props.onChange &&
						props.onChange(
							fromJS({
								input: "",
								pool: multiVal.map((val) => ({
									id: val.value,
									name: val.label,
								})),
							}) as any,
							false
						);
				} else {
					const existingValues = props.value as any;
					const removedValue = existingValues
						.filter((item) => {
							return !multiVal.map((val) => val.value).includes(item.get("id"));
						})
						.toJS();
					props.onDelete && props.onDelete(removedValue[0].id);
				}
				break;
			}
			case "clear":
				props.onChange && props.onChange("", false);
				break;
			default:
				break;
		}
	};

	/**
	 * If the value is a string, then there is no selection so there is no
	 * "value" as far as the picker is concerned. If the value is an
	 * immutable map, then we need to convert it to a plain object so that
	 * the picker can use it.
	 */
	const value = useMemo(() => {
		if (typeof props.value === "string") {
			return props.value;
		}

		if (isMultiSelect && props.value) {
			const immutableList = props.value as Immutable.List<Immutable.Map<string, string>>;
			return immutableList
				.map((item) => {
					return {
						value: item.get("id"),
						label: item.get("name"),
					};
				})
				.toJS();
		} else if (isImmutable(props.value)) {
			const immutableMap = props.value as Immutable.Map<string, string>;
			return {
				value: immutableMap.get("id"),
				label: immutableMap.get("name"),
			};
		}
	}, [props.value]) as SelectOption | SelectOption[];

	let prependOptions: any[] = [];

	// if is immutable map, convert prepend options to js and format to react select values
	if (props.prependOptions && isImmutable(props.prependOptions)) {
		const immutablePrependOptions = fromJS(props.prependOptions) as Immutable.List<Immutable.Map<string, string>>;
		prependOptions = immutablePrependOptions
			.map((option) => ({ value: option.get("id"), label: option.get("name") }))
			.toJS();
	}
	// else, format prepend options to react select values
	else if (props.prependOptions && !isImmutable(props.prependOptions)) {
		const jsPrependOptions = props.prependOptions as { id: string; name: string }[];
		prependOptions = jsPrependOptions.map((option) => ({ value: option.id, label: option.name }));
	}

	return (
		<AjaxInput
			{...props}
			label={label}
			value={value}
			disabled={disabled}
			onChange={onChange}
			placeholder={placeholder}
			prependOptions={prependOptions}
			isMultiSelect={isMultiSelect}
			filters={filters}
		/>
	);
};

export default CompatibilityWrapper;
